// New-message notification — sound and tab-title flash — for the whole app.
//
// This used to live inside MessageSection.vue's socket handler, so it could
// only ever fire while the Messages page was mounted: a message arriving while
// the user was on the dashboard, a listing, or anywhere else made no sound at
// all. The socket is app-wide now (utils/socketClient.js), so the notification
// belongs at the same level.
//
// The second reason a notification could go silent: browsers refuse programmatic
// playback until the user has interacted with the document, and a socket event
// is not an interaction. `new Audio(...).play()` rejected with NotAllowedError
// and the old code only logged it. primeNotificationSound() unlocks the element
// on the first real gesture so later notifications are permitted.

import { getSocket } from "./socketClient";

const SOUND_URL = "/audio/notification.mp3";
const GESTURES = ["pointerdown", "keydown", "touchstart"];

let bound = null;            // { socket, event, handler }
let activeConversation = null;
let audio = null;
let unlocked = false;
let primed = false;
let flashTimer = null;
let flashLabel = "New Message!";
let baseTitle = null;

// ── Sound ────────────────────────────────────────────────────────────────────

function getAudio() {
  if (!audio) {
    audio = new Audio(SOUND_URL);
    audio.preload = "auto";
  }
  return audio;
}

/** Resolves true only when the element is genuinely unlocked. */
function unlockAudio() {
  if (unlocked) return Promise.resolve(true);
  const sound = getAudio();
  const wasMuted = sound.muted;

  // Play muted and immediately rewind: enough for the browser to mark the
  // element as user-activated, inaudible to the user.
  sound.muted = true;
  let started;
  try {
    started = sound.play();
  } catch (err) {
    sound.muted = wasMuted;
    return Promise.resolve(false);
  }

  if (!started || !started.then) {
    sound.muted = wasMuted;
    unlocked = true;
    return Promise.resolve(true);
  }

  return started
    .then(() => {
      sound.pause();
      sound.currentTime = 0;
      sound.muted = wasMuted;
      unlocked = true;
      return true;
    })
    .catch(() => {
      sound.muted = wasMuted;
      // A failed fetch sticks to the element, so every later play() would fail
      // against the same dead source. load() makes the next attempt re-request
      // it — which matters when the first attempt happened before sign-in.
      try {
        sound.load();
      } catch (err) {
        /* nothing more to do */
      }
      return false;
    });
}

/** Arm the unlock on the first user gesture. Safe to call more than once. */
export function primeNotificationSound() {
  if (unlocked || primed) return;
  primed = true;

  const onGesture = () => {
    unlockAudio().then((ok) => {
      // Stay armed if it didn't take. Spending the user's one gesture on a
      // failed attempt used to leave the sound permanently locked.
      if (!ok) return;
      GESTURES.forEach((type) => window.removeEventListener(type, onGesture));
      primed = false;
    });
  };

  GESTURES.forEach((type) =>
    window.addEventListener(type, onGesture, { passive: true })
  );
}

export function playNotificationSound() {
  const sound = getAudio();
  try {
    sound.currentTime = 0;
  } catch (err) {
    /* not seekable yet — play from wherever it is */
  }
  const started = sound.play();
  if (started && started.catch) {
    started.catch((err) =>
      console.log("[notify] sound blocked:", err?.name || err)
    );
  }
}

// ── Tab title ────────────────────────────────────────────────────────────────

/**
 * Flash the tab title so a background tab shows something changed. `label` is
 * the alternate text, e.g. "(3) New messages".
 */
export function startTitleFlash(label = "New Message!") {
  if (baseTitle === null) baseTitle = document.title;

  // Held in a variable the running interval reads, so a later call can swap in
  // a better label — the unread poll upgrades "New Message!" to "(3) New
  // messages" — instead of being ignored because a flash is already going.
  flashLabel = label;
  if (flashTimer) return;

  let showingLabel = false;
  flashTimer = setInterval(() => {
    document.title = showingLabel ? baseTitle : flashLabel;
    showingLabel = !showingLabel;
  }, 1000);
}

export function stopTitleFlash() {
  if (flashTimer) {
    clearInterval(flashTimer);
    flashTimer = null;
  }
  if (baseTitle !== null) document.title = baseTitle;
}

// ── Wiring ───────────────────────────────────────────────────────────────────

/**
 * The conversation currently on screen. A message from this person needs no
 * sound while the user is looking at the thread — but it still gets one if the
 * tab is in the background, which is what WhatsApp and Teams do.
 */
export function setActiveConversation(userID) {
  activeConversation = userID || null;
}

function isWatching(fromUserId) {
  if (!activeConversation || fromUserId !== activeConversation) return false;
  return document.visibilityState === "visible" && document.hasFocus();
}

/**
 * Bind the new-message listener to the shared socket. Idempotent: re-binds only
 * when the socket or the signed-in user changed, so calling it on every route
 * change costs nothing.
 */
export function startMessageNotifications() {
  const socket = getSocket();
  const userID = localStorage.getItem("userid");
  if (!socket || !userID) return;

  const event = `receiveMessage:${userID}`;
  if (bound && bound.socket === socket && bound.event === event) return;

  stopMessageNotifications();

  const handler = (message) => {
    if (!message || message.fromUserId === userID) return;
    if (isWatching(message.fromUserId)) return;

    playNotificationSound();
    startTitleFlash();
  };

  socket.on(event, handler);
  bound = { socket, event, handler };
}

export function stopMessageNotifications() {
  if (bound) {
    bound.socket.off(bound.event, bound.handler);
    bound = null;
  }
  stopTitleFlash();
  activeConversation = null;
}
